import Vue from 'vue'
import VueI18n from 'vue-i18n'

Vue.use(VueI18n)

export const LANG_JA = 'ja'
export const LANG_EN = 'en'
export const LANG_ES = 'es'
export const LANG_PT_BR = 'pt-BR'
export const LANG_ID = 'id'
export const LANG_FR = 'fr'
export const LANG_RU = 'ru'
export const LANG_DE = 'de'
export const LANG_ZH_Hant = 'zh-Hant'
export const LANG_TH = 'th'
export const LANG_MS = 'ms'
export const LANG_KO = 'ko'
export const DEFAULT_LANGUAGE = LANG_EN
export const availableLanguages = [
  LANG_JA,
  LANG_EN,
  LANG_ES,
  LANG_PT_BR,
  LANG_ID,
  LANG_FR,
  LANG_RU,
  LANG_DE,
  LANG_ZH_Hant,
  LANG_TH,
  LANG_MS,
  LANG_KO,
] as const

const fallbackLanguageCode: { [key: string]: Language } = {
  'ja-JP': LANG_JA,
  'en-US': LANG_EN,
  'es-ES': LANG_ES,
  pt: LANG_PT_BR,
  'id-ID': LANG_ID,
  'fr-FR': LANG_FR,
  'ru-RU': LANG_RU,
  'de-DE': LANG_DE,
  'zh-TW': LANG_ZH_Hant,
  'zh-HK': LANG_ZH_Hant,
  'th-TH': LANG_TH,
  'ms-MY': LANG_MS,
  'ko-KR': LANG_KO,
} as const

export type Language = (typeof availableLanguages)[number]

export const REGION_JP = 'JP'

export function isAvailableLanguage(language: string) {
  return (availableLanguages as ReadonlyArray<string>).includes(language)
}

export function isFallbackLanguage(language: string) {
  return Object.keys(fallbackLanguageCode).includes(language)
}

export function getLanguageHeader() {
  return { 'Accept-Language': i18n.locale }
}

// ブラウザの言語設定から言語を決定して返す
function getBrowserLanguage() {
  const languages = navigator.languages
  return selectAvailableLanguage(languages)
}

export function selectAvailableLanguage(languages: readonly string[]) {
  for (const lang of languages) {
    if (isAvailableLanguage(lang)) {
      return lang as Language
    } else if (isFallbackLanguage(lang)) {
      return fallbackLanguageCode[lang]
    }
  }
  return LANG_EN
}

export function createI18n() {
  return new VueI18n({
    locale: getBrowserLanguage(),
    fallbackLocale: LANG_EN,
    messages: {
      [LANG_JA]: require('./resources/ja.json'),
      [LANG_EN]: require('./resources/en.json'),
      [LANG_ES]: require('./resources/es.json'),
      [LANG_PT_BR]: require('./resources/pt-BR.json'),
      [LANG_ID]: require('./resources/id.json'),
      [LANG_FR]: require('./resources/fr.json'),
      [LANG_RU]: require('./resources/ru.json'),
      [LANG_DE]: require('./resources/de.json'),
      [LANG_ZH_Hant]: require('./resources/zh-Hant.json'),
      [LANG_TH]: require('./resources/th.json'),
      [LANG_MS]: require('./resources/ms.json'),
      [LANG_KO]: require('./resources/ko.json'),
    },
    pluralizationRules: {
      // ロシア語の対応
      [LANG_RU]: function (choice) {
        if (choice === 0) {
          return 2 // 0のケースは翻訳しないので、many扱いにする
        }
        const teen = choice > 10 && choice < 20
        const endsWithOne = choice % 10 === 1
        if (!teen && endsWithOne) {
          return 0 // ends with 1: singular (день)
        }
        if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
          return 1 // ends with 2-4: few (дня)
        }
        return 2 // all other cases: many (дней)
      },
    },
  })
}

const i18n = createI18n()

export default i18n

export function getLanguage(): Language {
  return i18n.locale as Language
}

export function setLanguage(language: string) {
  if (isAvailableLanguage(language)) {
    i18n.locale = language
  }
}

export function resetLanguage() {
  i18n.locale = getBrowserLanguage()
}

const suffixKeyMap: { [k: string]: string } = {
  one: 'rankOrdinalSuffixOne',
  two: 'rankOrdinalSuffixTwo',
  few: 'rankOrdinalSuffixFew',
  other: 'rankOrdinalSuffixOther',
}

// ランキングで使う序数詞のsuffixを返す(-st, -nd, -rd, -th or -位)
export function getRankOrdinalSuffix(rank: number) {
  const pr = new Intl.PluralRules(i18n.locale, { type: 'ordinal' })
  const key = suffixKeyMap[pr.select(rank)] || suffixKeyMap.other
  return i18n.t(key)
}
